import React from 'react'
import { Button, CircularProgress } from '@mui/material'
import { alpha, darken } from '@mui/material/styles'

import withCapabilities from '../../hoc/withCapabilities'

import type { ButtonProps } from '@mui/material'

/**
 * ActionButton Component
 *
 * A customizable button component with predefined styles for different actions and sizes.
 *
 * Available button types:
 * - 'primary' (default): Primary action button (contained, primary color)
 * - 'approve': Approval button (outlined, success color)
 * - 'reject': Rejection button (outlined, error color)
 * - 'secondary': Secondary action button (outlined, secondary color)
 *
 * Available sizes:
 * - 'small': Small-sized button
 * - 'medium': Medium-sized button
 * - 'large': Large-sized button
 * - 'fullWidth' (default): Full-width button
 *
 * Usage:
 * <ActionButton buttonType="approve" size="medium">Approve</ActionButton>
 */

interface ActionButtonProps extends Omit<ButtonProps, 'size'> {
  buttonType?: 'primary' | 'approve' | 'reject' | 'secondary'
  size?: 'small' | 'medium' | 'large' | 'fullWidth'
  isLoading?: boolean
}

type ActionButtonType = NonNullable<ActionButtonProps['buttonType']>

const getActionButtonSx =
  (buttonType: ActionButtonType): ButtonProps['sx'] =>
  (theme) => {
    const baseStyles = {
      textTransform: 'uppercase' as const,
    }

    if (buttonType === 'primary' && theme.palette.branding.enabled) {
      return {
        ...baseStyles,
        backgroundColor: theme.palette.branding.button,
        color: theme.palette.branding.buttonText,
        '&:hover': {
          backgroundColor: darken(theme.palette.branding.button, 0.2),
          color: theme.palette.branding.buttonText,
        },
      }
    }

    if (buttonType === 'secondary') {
      const backgroundColor = theme.palette.background.secondary

      if (theme.palette.branding.enabled) {
        return {
          ...baseStyles,
          color: theme.palette.branding.accentColor,
          borderColor: alpha(theme.palette.branding.accentColor, 0.5),
          backgroundColor,
          '&:hover': {
            borderColor: theme.palette.branding.accentColor,
            backgroundColor,
          },
        }
      }

      return {
        ...baseStyles,
        backgroundColor,
        '&:hover': {
          backgroundColor,
        },
      }
    }

    return baseStyles
  }

const ActionButton: React.FC<ActionButtonProps> = ({
  children,
  buttonType = 'primary',
  size: buttonSize = 'fullWidth',
  isLoading = false,
  sx,
  ...props
}) => {
  let variant: 'contained' | 'outlined'
  let color: 'primary' | 'secondary' | 'error' | 'success'
  switch (buttonType) {
    case 'approve':
      variant = 'outlined'
      color = 'success'
      break
    case 'reject':
      variant = 'outlined'
      color = 'error'
      break
    case 'secondary':
      variant = 'outlined'
      color = 'secondary'
      break
    default:
      variant = 'contained'
      color = 'primary'
  }

  return (
    <Button
      variant={variant}
      color={color}
      {...props}
      {...(buttonSize === 'fullWidth' ? { style: { width: '100%' } } : { size: buttonSize })}
      sx={[getActionButtonSx(buttonType), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      {children} {isLoading ? <CircularProgress size={24} sx={{ marginLeft: 2, color: 'text.greyOut' }} /> : null}
    </Button>
  )
}

export default withCapabilities(ActionButton)
